import { Flex } from '@grupoboticario/flora-react';
import { ChevronRightIcon } from '@grupoboticario/flora-react-icons';
import { Link } from 'react-router-dom';

import { ResponsiveFloraTypography } from 'components';
import { BRANDS } from 'config/environments/shared';
import { getLinksFromMarkdown } from 'pages/Profile/helpers';
import { APP_PATHS } from 'routes';
import { useGlobalContext } from 'store/contexts';

import type { ResellerRegistrationMessage } from 'domains/reseller-registration';

type FormattedMessageWithLinkProps = {
  message?: string;
  messageObject?: ResellerRegistrationMessage;
};

type PartType = 'text' | 'break' | 'link';

const TextParts = ({ content }: { content: string }) => {
  return (
    <ResponsiveFloraTypography
      color='$nonInteractivePredominant'
      fontSize='bodySmallShort'
      fontWeight='regular'
      as='span'
    >
      {content}
    </ResponsiveFloraTypography>
  );
};

const BreakParts = () => {
  return <br />;
};

const LinkParts = ({ url, text }: { url: string; text: string }) => {
  return (
    <Link to={url} target='_blank' rel='noopener noreferrer'>
      <ResponsiveFloraTypography
        color='$backgroundAltPrimary'
        as='span'
        fontSize='bodySmallStandard'
        fontWeight='regular'
      >
        {text}
      </ResponsiveFloraTypography>
    </Link>
  );
};

const ActionsComponent = ({ messageObject }: { messageObject: ResellerRegistrationMessage }) => {
  const { brand: environmentBrand } = useGlobalContext();
  const isEud = BRANDS.EUD === environmentBrand.short;
  const url = isEud ? APP_PATHS.RESELLER_PLACE_EUD : APP_PATHS.RESELLER_PLACE_BOT;
  const firstAction = messageObject?.actions?.[0];

  return (
    <>
      {messageObject?.message && <TextParts content={messageObject?.message} />}
      {firstAction && (
        <>
          <br />
          <br />
          <Link to={url}>
            <ResponsiveFloraTypography
              color='$backgroundAltPrimary'
              fontSize='bodySmallStandard'
              fontWeight='regular'
            >
              <Flex gap='$2' justify='left' align='center'>
                {messageObject?.actions?.[0].buttonText}{' '}
                <ChevronRightIcon color='$backgroundAltPrimary' size={16} aria-hidden />
              </Flex>
            </ResponsiveFloraTypography>
          </Link>
        </>
      )}
    </>
  );
};

export const FormattedMessageWithLink = ({
  message,
  messageObject,
}: FormattedMessageWithLinkProps) => {
  const isActions = (messageObject?.actions?.length ?? 0) > 0;

  if (isActions && messageObject) {
    return <ActionsComponent messageObject={messageObject} />;
  }

  const parts = getLinksFromMarkdown(message || '');

  if (!parts) {
    return null;
  }

  const PARTS_ENUM: Record<PartType, React.ComponentType<React.PropsWithChildren<any>>> = {
    text: TextParts,
    break: BreakParts,
    link: LinkParts,
  };

  return (
    <>
      {parts?.map((part, index) => {
        const PartComponent = PARTS_ENUM[part?.type as PartType];
        if (!PartComponent) {
          return null;
        }

        return <PartComponent key={`${index}-${part?.type}`} {...part} />;
      })}
    </>
  );
};

export default FormattedMessageWithLink;
